import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCompanyStore } from '@store'
import { Save, Send, X, Building2, User, MapPin, ChevronDown, ChevronUp, Package, Calendar } from 'lucide-react'
import { Modal, Button, Alert, Input, Select } from '@components/common'
import './CompanyForm.css'

/**
 * Create Company Form Validation Schema
 */
const createCompanySchema = z.object({
	// Required: Company Information
	company_name: z.string()
		.min(2, 'Company name must be at least 2 characters')
		.max(255, 'Company name cannot exceed 255 characters'),
	package_id: z.number({
		required_error: 'Please select a package',
		invalid_type_error: 'Please select a package',
	}).positive('Please select a package'),

	// Required: Compliance Manager (Create mode only)
	cm_first_name: z.string()
		.min(2, 'First name must be at least 2 characters')
		.max(100, 'First name cannot exceed 100 characters'),
	cm_last_name: z.string()
		.min(2, 'Last name must be at least 2 characters')
		.max(100, 'Last name cannot exceed 100 characters'),
	cm_email: z.string()
		.email('Please enter a valid email address')
		.max(255, 'Email cannot exceed 255 characters'),

	// Optional fields
	industry: z.string().max(100).optional().or(z.literal('')),
	company_size: z.string().optional().or(z.literal('')),
	phone: z.string().max(20).optional().or(z.literal('')),
	website: z.string()
		.refine((val) => !val || val === '' || /^https?:\/\/.+/.test(val), {
			message: 'Website must be a valid URL (http:// or https://)',
		})
		.optional()
		.or(z.literal('')),
	cm_phone: z.string().max(20).optional().or(z.literal('')),
	address: z.string().optional().or(z.literal('')),
	city: z.string().max(100).optional().or(z.literal('')),
	state: z.string().max(100).optional().or(z.literal('')),
	country: z.string().max(100).optional().or(z.literal('')),
	postal_code: z.string().max(20).optional().or(z.literal('')),
})

/**
 * Edit Company Form Validation Schema
 */
const editCompanySchema = z.object({
	// Required: Company Information
	name: z.string()
		.min(2, 'Company name must be at least 2 characters')
		.max(255, 'Company name cannot exceed 255 characters'),
	package_id: z.number({
		required_error: 'Please select a package',
		invalid_type_error: 'Please select a package',
	}).positive('Please select a package'),

	// Required: Compliance Manager (Edit mode - dropdown)
	compliance_manager_id: z.number({
		required_error: 'Please select a compliance manager',
		invalid_type_error: 'Please select a compliance manager',
	}).positive('Please select a compliance manager'),

	// Optional: Subscription dates
	subscription_start: z.string().optional().or(z.literal('')),
	subscription_end: z.string().optional().or(z.literal('')),

	// Optional fields
	email: z.string().email().max(255).optional().or(z.literal('')),
	industry: z.string().max(100).optional().or(z.literal('')),
	company_size: z.string().optional().or(z.literal('')),
	phone: z.string().max(20).optional().or(z.literal('')),
	website: z.string()
		.refine((val) => !val || val === '' || /^https?:\/\/.+/.test(val), {
			message: 'Website must be a valid URL (http:// or https://)',
		})
		.optional()
		.or(z.literal('')),
	address: z.string().optional().or(z.literal('')),
	city: z.string().max(100).optional().or(z.literal('')),
	state: z.string().max(100).optional().or(z.literal('')),
	country: z.string().max(100).optional().or(z.literal('')),
	postal_code: z.string().max(20).optional().or(z.literal('')),
})

/**
 * Company Form Component
 *
 * Supports both Create and Edit modes
 *
 * @param {Object} company - Company data for edit mode (null for create)
 * @param {Function} onClose - Close modal callback
 */
const CompanyForm = ({ company = null, onClose }) => {
	const isEditMode = !!company
	const { packages, createCompany, updateCompany } = useCompanyStore()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState(null)
	const [showAddress, setShowAddress] = useState(false)

	// Debug: Log when form opens
	useEffect(() => {
		console.log('CompanyForm opened:', {
			isEditMode,
			company: company?.name,
			packagesCount: packages?.length,
			selectedPackageId: company?.package_id,
			subscriptionStart: company?.subscription_start
		})
	}, [])

	// Debug: Log packages when they change
	useEffect(() => {
		console.log('Packages loaded:', packages?.length, 'packages')
	}, [packages])

	// Industry options
	const industryOptions = [
		'Technology',
		'Healthcare',
		'Finance',
		'Manufacturing',
		'Retail',
		'Education',
		'Government',
		'Non-Profit',
		'Other',
	]

	// Company size options
	const companySizeOptions = [
		'1-10',
		'11-50',
		'51-200',
		'201-500',
		'501-1000',
		'1000+',
	]

	// Country options
	const countryOptions = [
		'United States',
		'United Kingdom',
		'Canada',
		'Australia',
		'Germany',
		'France',
		'India',
		'Singapore',
		'Other',
	]

	// Initialize form with React Hook Form + Zod
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		watch,
		setValue,
		reset,
	} = useForm({
		resolver: zodResolver(isEditMode ? editCompanySchema : createCompanySchema),
		mode: 'onChange',
		defaultValues: isEditMode ? {
			name: '',
			package_id: 0,
			compliance_manager_id: 0,
			email: '',
			phone: '',
			website: '',
			industry: '',
			company_size: '',
			subscription_start: '',
			subscription_end: '',
			address: '',
			city: '',
			state: '',
			country: '',
			postal_code: '',
		} : {
			company_name: '',
			package_id: 0,
			industry: '',
			company_size: '',
			phone: '',
			website: '',
			cm_first_name: '',
			cm_last_name: '',
			cm_email: '',
			cm_phone: '',
			address: '',
			city: '',
			state: '',
			country: '',
			postal_code: '',
		}
	})

	const selectedPackageId = watch('package_id')
	const subscriptionStart = watch('subscription_start')

	// Populate form with company data in Edit mode
	useEffect(() => {
		if (isEditMode && company) {
			reset({
				name: company.name || '',
				package_id: Number(company.package_id) || 0,
				compliance_manager_id: Number(company.compliance_manager_id) || 0,
				email: company.email || '',
				phone: company.phone || '',
				website: company.website || '',
				industry: company.industry || '',
				company_size: company.company_size || '',
				subscription_start: company.subscription_start || '',
				subscription_end: company.subscription_end || '',
				address: company.address || '',
				city: company.city || '',
				state: company.state || '',
				country: company.country || '',
				postal_code: company.postal_code || '',
			})
		}
	}, [isEditMode, company, reset])

	// Auto-expand address section if there's address data in edit mode
	useEffect(() => {
		if (isEditMode && company && (company.address || company.city || company.state)) {
			setShowAddress(true)
		}
	}, [isEditMode, company])

	// Auto-populate subscription end date when start date or package changes (only in Edit mode)
	// Automatically calculates end date based on package billing cycle
	useEffect(() => {
		// Only trigger in edit mode
		if (!isEditMode) return

		// Only if start date is set and not empty
		if (!subscriptionStart || subscriptionStart === '') return

		// Need a selected package ID
		if (!selectedPackageId || selectedPackageId === 0) return

		// Need packages to be loaded
		if (!packages || packages.length === 0) return

		// Find the selected package (handle both number and string IDs)
		const selectedPackage = packages.find(pkg => Number(pkg.id) === Number(selectedPackageId))

		if (!selectedPackage) {
			console.warn('Package not found for ID:', selectedPackageId)
			return
		}

		console.log('Auto-calculating end date:', {
			startDate: subscriptionStart,
			packageId: selectedPackageId,
			billingCycle: selectedPackage.billing_cycle
		})

		// Parse the start date (format: YYYY-MM-DD)
		const startDate = new Date(subscriptionStart + 'T00:00:00')
		const endDate = new Date(startDate)

		// Calculate end date based on billing cycle
		switch (selectedPackage.billing_cycle) {
			case 'monthly':
				endDate.setMonth(endDate.getMonth() + 1)
				break
			case 'yearly':
				endDate.setFullYear(endDate.getFullYear() + 1)
				break
			default:
				// Default to yearly if billing cycle is not recognized
				console.warn('Unknown billing cycle:', selectedPackage.billing_cycle)
				endDate.setFullYear(endDate.getFullYear() + 1)
		}

		// Format date as YYYY-MM-DD for input[type="date"]
		const formattedEndDate = endDate.toISOString().split('T')[0]

		console.log('Setting end date to:', formattedEndDate)
		// Use setValue with all options to ensure the field updates visibly
		setValue('subscription_end', formattedEndDate, {
			shouldValidate: true,
			shouldDirty: true,
			shouldTouch: true
		})
	}, [isEditMode, subscriptionStart, selectedPackageId, packages, setValue])

	/**
	 * Handle form submission
	 * @param {Object} data - Form data
	 * @param {boolean} isDraft - If true, save as draft (pending status), otherwise send activation email
	 */
	const onSubmit = async (data, isDraft = false) => {
		setIsSubmitting(true)
		setSubmitError(null)

		try {
			if (isEditMode) {
				// Update company
				await updateCompany(company.id, data)
				onClose()
			} else {
				// Create company with appropriate flag
				// send_activation: true = Save & Send Activation (sends email)
				// send_activation: false = Save as Draft (pending status, no email)
				const payload = {
					...data,
					send_activation: !isDraft
				}
				console.log('Creating company with payload:', payload)
				console.log('isDraft:', isDraft, '=> send_activation:', !isDraft)
				await createCompany(payload)
				onClose()
			}
		} catch (error) {
			console.error('Form submission error:', error)
			setSubmitError(error.message || 'Failed to save company. Please try again.')
		} finally {
			setIsSubmitting(false)
		}
	}

	/**
	 * Handle package selection
	 */
	const handlePackageSelect = (packageId) => {
		setValue('package_id', packageId, { shouldValidate: true, shouldDirty: true })
	}

	/**
	 * Modal Footer with Action Buttons
	 */
	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
				<X size={18} />
				<span>Cancel</span>
			</Button>

			{isEditMode ? (
				<Button
					variant="primary"
					onClick={handleSubmit(onSubmit)}
					disabled={isSubmitting || !isDirty}
					loading={isSubmitting}
				>
					<Save size={18} />
					<span>Save Changes</span>
				</Button>
			) : (
				<>
					<Button
						variant="secondary"
						onClick={handleSubmit((data) => onSubmit(data, true))}
						disabled={isSubmitting}
						loading={isSubmitting}
					>
						<Save size={18} />
						<span>Save as Draft</span>
					</Button>

					<Button
						variant="primary"
						onClick={handleSubmit((data) => onSubmit(data, false))}
						disabled={isSubmitting}
						loading={isSubmitting}
					>
						<Send size={18} />
						<span>Save & Send Activation</span>
					</Button>
				</>
			)}
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title={isEditMode ? 'Edit Company' : 'Add New Company'}
			size="large"
			footer={footer}
			closeOnBackdrop={false}
			closeOnEscape={false}
		>
			<form className="company-form" onSubmit={handleSubmit(onSubmit)}>
				{/* Error Alert */}
				{submitError && (
					<div className="company-form__alert">
						<Alert variant="danger" message={submitError} />
					</div>
				)}

				{/* ============================================================ */}
				{/* SECTION 1: Company Information */}
				{/* ============================================================ */}
				<div className="company-form__section">
					<div className="company-form__section-header">
						<Building2 size={20} />
						<h3 className="company-form__section-title">Company Information</h3>
					</div>

					<div className="company-form__grid">
						{/* Company Name */}
						<div className="form-field form-field--full">
							<Input
								type="text"
								label="Company Name"
								placeholder="Enter company name"
								required
								error={errors[isEditMode ? 'name' : 'company_name']?.message}
								{...register(isEditMode ? 'name' : 'company_name')}
							/>
						</div>

						{/* Package Selection */}
						<div className="form-field form-field--full">
							<label className="form-field__label">
								Package <span className="form-field__required">*</span>
							</label>
							<input type="hidden" {...register('package_id', { valueAsNumber: true })} />
							<div className="package-selection">
								{packages.map((pkg) => (
									<div
										key={pkg.id}
										className={`package-card ${selectedPackageId === Number(pkg.id) ? 'package-card--selected' : ''}`}
										onClick={() => handlePackageSelect(Number(pkg.id))}
									>
										<div className="package-card__header">
											<Package size={20} />
										</div>
										<h4 className="package-card__title">{pkg.title || pkg.name}</h4>
										<div className="package-card__price">
											${pkg.price}/{pkg.billing_cycle}
										</div>
									</div>
								))}
							</div>
							{errors.package_id && (
								<span className="form-field__error">{errors.package_id.message}</span>
							)}
						</div>

						{/* Industry */}
						<div className="form-field">
							<Select
								label="Industry"
								placeholder="Select industry"
								options={industryOptions.map(industry => ({
									value: industry,
									label: industry
								}))}
								{...register('industry')}
							/>
						</div>

						{/* Company Size */}
						<div className="form-field">
							<Select
								label="Company Size"
								placeholder="Select size"
								options={companySizeOptions.map(size => ({
									value: size,
									label: `${size} employees`
								}))}
								{...register('company_size')}
							/>
						</div>

						{/* Phone */}
						<div className="form-field">
							<Input
								type="tel"
								label="Phone"
								placeholder="+1 (555) 123-4567"
								error={errors.phone?.message}
								{...register('phone')}
							/>
						</div>

						{/* Website */}
						<div className="form-field">
							<Input
								type="url"
								label="Website"
								placeholder="https://example.com"
								error={errors.website?.message}
								{...register('website')}
							/>
						</div>
					</div>
				</div>

				{/* ============================================================ */}
				{/* SECTION 2: Subscription Details (Edit Mode Only) */}
				{/* ============================================================ */}
				{isEditMode && (
					<div className="company-form__section">
						<div className="company-form__section-header">
							<Calendar size={20} />
							<h3 className="company-form__section-title">Subscription Details</h3>
						</div>

						<div className="company-form__grid">
							{/* Subscription Start */}
							<div className="form-field">
								<Input
									type="date"
									label="Subscription Start Date"
									error={errors.subscription_start?.message}
									{...register('subscription_start')}
								/>
							</div>

							{/* Subscription End */}
							<div className="form-field">
								<Input
									type="date"
									label="Subscription End Date"
									error={errors.subscription_end?.message}
									{...register('subscription_end')}
								/>
							</div>
						</div>
					</div>
				)}

				{/* ============================================================ */}
				{/* SECTION 3: Compliance Manager */}
				{/* ============================================================ */}
				<div className="company-form__section">
					<div className="company-form__section-header">
						<User size={20} />
						<h3 className="company-form__section-title">Compliance Manager</h3>
					</div>

					{isEditMode ? (
						// Edit Mode: Dropdown to select from company users
						<div className="company-form__grid">
							<div className="form-field form-field--full">
								<Select
									label="Select Compliance Manager"
									placeholder="Select compliance manager"
									required
									options={[
										{ value: 0, label: 'Select compliance manager' },
										...(company.users?.map(user => ({
											value: user.id,
											label: `${user.full_name} (${user.email})`
										})) || [])
									]}
									error={errors.compliance_manager_id?.message}
									{...register('compliance_manager_id', { valueAsNumber: true })}
								/>
							</div>

							{/* Email (optional in edit mode) */}
							<div className="form-field">
								<Input
									type="email"
									label="Company Email"
									placeholder="company@example.com"
									error={errors.email?.message}
									{...register('email')}
								/>
							</div>
						</div>
					) : (
						// Create Mode: Input fields for new CM
						<div className="company-form__grid">
							<div className="form-field">
								<Input
									type="text"
									label="First Name"
									placeholder="John"
									required
									error={errors.cm_first_name?.message}
									{...register('cm_first_name')}
								/>
							</div>

							<div className="form-field">
								<Input
									type="text"
									label="Last Name"
									placeholder="Doe"
									required
									error={errors.cm_last_name?.message}
									{...register('cm_last_name')}
								/>
							</div>

							<div className="form-field">
								<Input
									type="email"
									label="Email"
									placeholder="john.doe@example.com"
									required
									error={errors.cm_email?.message}
									{...register('cm_email')}
								/>
							</div>

							<div className="form-field">
								<Input
									type="tel"
									label="Phone (Optional)"
									placeholder="+1 (555) 123-4567"
									error={errors.cm_phone?.message}
									{...register('cm_phone')}
								/>
							</div>
						</div>
					)}
				</div>

				{/* ============================================================ */}
				{/* SECTION 4: Address (Collapsible) */}
				{/* ============================================================ */}
				<div className="company-form__section">
					<button
						type="button"
						className="company-form__section-toggle"
						onClick={() => setShowAddress(!showAddress)}
					>
						<div className="company-form__section-header">
							<MapPin size={20} />
							<h3 className="company-form__section-title">Address (Optional)</h3>
						</div>
						{showAddress ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
					</button>

					{showAddress && (
						<div className="company-form__grid">
							<div className="form-field form-field--full">
								<Input
									type="text"
									label="Street Address"
									placeholder="123 Main Street"
									{...register('address')}
								/>
							</div>

							<div className="form-field">
								<Input
									type="text"
									label="City"
									placeholder="San Francisco"
									error={errors.city?.message}
									{...register('city')}
								/>
							</div>

							<div className="form-field">
								<Input
									type="text"
									label="State/Province"
									placeholder="California"
									error={errors.state?.message}
									{...register('state')}
								/>
							</div>

							<div className="form-field">
								<Select
									label="Country"
									placeholder="Select country"
									options={countryOptions.map(country => ({
										value: country,
										label: country
									}))}
									{...register('country')}
								/>
							</div>

							<div className="form-field">
								<Input
									type="text"
									label="Postal Code"
									placeholder="94102"
									error={errors.postal_code?.message}
									{...register('postal_code')}
								/>
							</div>
						</div>
					)}
				</div>
			</form>
		</Modal>
	)
}

export default CompanyForm
